/**
 * ClientProfileForm - TASK-376
 * 5-section form for Client Profile: Account Details, Management,
 * Beneficial Ownership, Power of Attorney, Account Structure.
 * Auto-populated fields from approved KYC show source='inherited' badge.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  Settings,
  UserCheck,
  Scale,
  Building2,
  Save,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader';
import { AccountDetailsSection } from './AccountDetailsSection';
import { ManagementSection } from './ManagementSection';
import { AccountStructureSection } from './AccountStructureSection';
import { BeneficialOwnerSection } from './BeneficialOwnerSection';
import type { BeneficialOwnerEntry } from './BeneficialOwnerSection';
import { POASection } from './POASection';
import type { POAData } from './POASection';
import kycService from '../../../services/kycService';

const clientProfileCompletionSchema = z.object({
  account_details: z.object({
    holder_name: z.string().min(1, 'Account holder name is required'),
    date_of_birth: z.string().min(1, 'Date of birth is required'),
    nationality: z.string().min(1, 'Nationality is required'),
    email: z.string().email('Valid email is required'),
    phone: z.string().min(1, 'Phone number is required'),
  }),
});

type ValidationErrors = Record<string, string>;

interface ClientProfileFormProps {
  clientId: string;
  clientName: string;
  initialData?: ClientProfileFormData | null;
  onSaved?: () => void;
}

export interface ClientProfileFormData {
  account_details: {
    holder_name?: string | null;
    date_of_birth?: string | null;
    nationality?: string | null;
    address?: Record<string, any> | null;
    phone?: string | null;
    email?: string | null;
    tax_residence?: string | null;
    us_person?: boolean;
    account_type?: string | null;
    currency?: string | null;
    source?: string;
  };
  management: {
    type?: 'discretionary' | 'advisory' | null;
    strategy?: string | null;
    reporting_frequency?: string | null;
    source?: string;
  };
  beneficial_ownership: {
    type?: 'self' | 'third_party';
    owners: BeneficialOwnerEntry[];
    source?: string;
  };
  power_of_attorney: POAData;
  account_structure: {
    structure_type?: string | null;
    details?: string | null;
    source?: string;
  };
  status: 'draft' | 'completed';
  generated_at?: string;
  generated_from?: string;
}

const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  sectionNumber: number;
  isOpen: boolean;
  onToggle: () => void;
  isInherited?: boolean;
}> = (props) => (
  <CollapsibleSectionHeader
    icon={props.icon}
    title={props.title}
    label={props.sectionNumber}
    isOpen={props.isOpen}
    onToggle={props.onToggle}
    isInherited={props.isInherited}
  />
);

const DEFAULT_DATA: ClientProfileFormData = {
  account_details: {
    holder_name: null,
    date_of_birth: null,
    nationality: null,
    address: null,
    phone: null,
    email: null,
    tax_residence: null,
    us_person: false,
    account_type: null,
    currency: null,
    source: 'manual',
  },
  management: {
    type: null,
    strategy: null,
    reporting_frequency: null,
    source: 'manual',
  },
  beneficial_ownership: {
    type: 'self',
    owners: [],
    source: 'manual',
  },
  power_of_attorney: {
    holder_name: '',
    relationship: '',
    scope: '',
    date_signed: '',
    source: 'manual',
  },
  account_structure: {
    structure_type: null,
    details: null,
    source: 'manual',
  },
  status: 'draft',
};

export const ClientProfileForm: React.FC<ClientProfileFormProps> = ({
  clientId,
  clientName,
  initialData,
  onSaved,
}) => {
  const [formData, setFormData] = useState<ClientProfileFormData>(
    initialData || DEFAULT_DATA
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({
    1: true,
    2: false,
    3: false,
    4: false,
    5: false,
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const toggleSection = useCallback((section: number) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const updateAccountDetails = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        account_details: { ...prev.account_details, [field]: value },
      }));
    },
    []
  );

  const updateManagement = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        management: { ...prev.management, [field]: value },
      }));
    },
    []
  );

  const updateStructure = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        account_structure: { ...prev.account_structure, [field]: value },
      }));
    },
    []
  );

  const handleOwnerChange = useCallback(
    (index: number, field: keyof BeneficialOwnerEntry, value: string | number) => {
      setFormData((prev) => {
        const owners = [...prev.beneficial_ownership.owners];
        owners[index] = { ...owners[index], [field]: value };
        return {
          ...prev,
          beneficial_ownership: { ...prev.beneficial_ownership, owners },
        };
      });
    },
    []
  );

  const handleAddOwner = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      beneficial_ownership: {
        ...prev.beneficial_ownership,
        owners: [
          ...prev.beneficial_ownership.owners,
          {
            name: '',
            date_of_birth: '',
            nationality: '',
            address: '',
            ownership_percentage: 0,
            source: 'manual',
          },
        ],
      },
    }));
  }, []);

  const handleRemoveOwner = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      beneficial_ownership: {
        ...prev.beneficial_ownership,
        owners: prev.beneficial_ownership.owners.filter((_, i) => i !== index),
      },
    }));
  }, []);

  const handlePOAChange = useCallback((field: keyof POAData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      power_of_attorney: { ...prev.power_of_attorney, [field]: value },
    }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const result = clientProfileCompletionSchema.safeParse(formData);
    if (result.success) {
      setErrors({});
      return true;
    }
    const fieldErrors: ValidationErrors = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      fieldErrors[path] = issue.message;
    }
    setErrors(fieldErrors);
    return false;
  }, [formData]);

  const handleSave = async (status: 'draft' | 'completed') => {
    if (status === 'completed' && !validateForm()) {
      toast.error('Please fill in all required fields before completing');
      setOpenSections((prev) => ({ ...prev, 1: true }));
      return;
    }

    setSaving(true);
    try {
      const dataToSave = { ...formData, status };
      await kycService.saveClientProfile(clientId, dataToSave);
      toast.success(
        status === 'completed'
          ? 'Client Profile completed and saved'
          : 'Client Profile draft saved'
      );
      setErrors({});
      setFormData(dataToSave);
      onSaved?.();
    } catch (error: unknown) {
      const message =
        (error as Record<string, any>)?.response?.data?.detail || 'Failed to save Client Profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const isInherited = (source?: string) => source === 'inherited';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Client Profile</h2>
          <p className="text-sm text-gray-500">
            {clientName} — {formData.status === 'completed' ? 'Completed' : 'Draft'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave('completed')}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Complete
          </button>
        </div>
      </div>

      {/* Section 1: Account Details */}
      <div className="border border-gray-200 rounded-lg">
        <SectionHeader
          icon={User}
          title="Account Details"
          sectionNumber={1}
          isOpen={!!openSections[1]}
          onToggle={() => toggleSection(1)}
          isInherited={isInherited(formData.account_details.source)}
        />
        {openSections[1] && (
          <AccountDetailsSection
            data={formData.account_details}
            onChange={updateAccountDetails}
            errors={errors}
            isInherited={isInherited(formData.account_details.source)}
          />
        )}
      </div>

      {/* Section 2: Management */}
      <div className="border border-gray-200 rounded-lg">
        <SectionHeader
          icon={Settings}
          title="Management Guidelines"
          sectionNumber={2}
          isOpen={!!openSections[2]}
          onToggle={() => toggleSection(2)}
        />
        {openSections[2] && (
          <ManagementSection
            data={formData.management}
            onChange={updateManagement}
          />
        )}
      </div>

      {/* Section 3: Beneficial Ownership */}
      <div className="border border-gray-200 rounded-lg">
        <SectionHeader
          icon={UserCheck}
          title="Beneficial Ownership"
          sectionNumber={3}
          isOpen={!!openSections[3]}
          onToggle={() => toggleSection(3)}
          isInherited={isInherited(formData.beneficial_ownership.source)}
        />
        {openSections[3] && (
          <div className="px-4 pb-4">
            <BeneficialOwnerSection
              owners={formData.beneficial_ownership.owners}
              onAdd={handleAddOwner}
              onRemove={handleRemoveOwner}
              onChange={handleOwnerChange}
            />
          </div>
        )}
      </div>

      {/* Section 4: Power of Attorney */}
      <div className="border border-gray-200 rounded-lg">
        <SectionHeader
          icon={Scale}
          title="Power of Attorney"
          sectionNumber={4}
          isOpen={!!openSections[4]}
          onToggle={() => toggleSection(4)}
        />
        {openSections[4] && (
          <div className="px-4 pb-4">
            <POASection data={formData.power_of_attorney} onChange={handlePOAChange} />
          </div>
        )}
      </div>

      {/* Section 5: Account Structure */}
      <div className="border border-gray-200 rounded-lg">
        <SectionHeader
          icon={Building2}
          title="Account Structure"
          sectionNumber={5}
          isOpen={!!openSections[5]}
          onToggle={() => toggleSection(5)}
        />
        {openSections[5] && (
          <AccountStructureSection
            data={formData.account_structure}
            onChange={updateStructure}
          />
        )}
      </div>
    </div>
  );
};

export default ClientProfileForm;
